/** Fonds de discussion Chat — presets CSS + photo perso, préférences par conversation (local). */

export const CHAT_WALLPAPER_STORAGE_KEY = "chat_wallpaper_prefs_v1";

/** Taille max image source avant compression (aligné PJ / avatar : 10 Mo). */
export const CHAT_WALLPAPER_MAX_SOURCE_BYTES = 10 * 1024 * 1024;

export const CHAT_WALLPAPER_IDS = [
  "none",
  "custom",
  "mist-blue",
  "soft-mint",
  "peach-glow",
  "lavender",
  "sand",
  "aurora",
  "stripes-soft",
  "stripes-diag",
  "waves",
  "dots",
] as const;

export type ChatWallpaperId = (typeof CHAT_WALLPAPER_IDS)[number];

export type ChatWallpaperPreset = {
  id: ChatWallpaperId;
  label: string;
  /** Aperçu miniature (CSS background). */
  preview: string;
};

export type ChatWallpaperPrefs = {
  id: ChatWallpaperId;
  /** 0–100 */
  opacity: number;
  /** 0–100 — flou CSS (0 = net). */
  blur: number;
  /** Data URL JPEG si id === "custom" (stocké en localStorage). */
  customUrl?: string | null;
};

const DEFAULT_PREFS: ChatWallpaperPrefs = { id: "none", opacity: 45, blur: 0, customUrl: null };

export const CHAT_WALLPAPER_PRESETS: Record<ChatWallpaperId, ChatWallpaperPreset> = {
  none: {
    id: "none",
    label: "Aucun",
    preview: "var(--chat-bg)",
  },
  custom: {
    id: "custom",
    label: "Ma photo",
    preview: "var(--chat-surface-elevated)",
  },
  "mist-blue": {
    id: "mist-blue",
    label: "Brume bleue",
    preview: "linear-gradient(160deg, #dbeafe 0%, #eff6ff 45%, #e0f2fe 100%)",
  },
  "soft-mint": {
    id: "soft-mint",
    label: "Menthe",
    preview: "linear-gradient(165deg, #d1fae5 0%, #ecfdf5 50%, #ccfbf1 100%)",
  },
  "peach-glow": {
    id: "peach-glow",
    label: "Pêche",
    preview: "linear-gradient(150deg, #ffedd5 0%, #fff7ed 48%, #fce7f3 100%)",
  },
  lavender: {
    id: "lavender",
    label: "Lavande",
    preview: "linear-gradient(155deg, #ede9fe 0%, #f5f3ff 50%, #e0e7ff 100%)",
  },
  sand: {
    id: "sand",
    label: "Sable",
    preview: "linear-gradient(170deg, #f5e6d3 0%, #faf5ef 55%, #efe6dc 100%)",
  },
  aurora: {
    id: "aurora",
    label: "Aurore",
    preview:
      "linear-gradient(135deg, #c7d2fe 0%, #ddd6fe 35%, #fbcfe8 65%, #a5f3fc 100%)",
  },
  "stripes-soft": {
    id: "stripes-soft",
    label: "Rayures douces",
    preview:
      "repeating-linear-gradient(90deg, #e8eef8 0 14px, #f4f7fc 14px 28px)",
  },
  "stripes-diag": {
    id: "stripes-diag",
    label: "Rayures diagonales",
    preview:
      "repeating-linear-gradient(-45deg, #e8f0fe 0 12px, #f7fafc 12px 24px)",
  },
  waves: {
    id: "waves",
    label: "Vagues",
    preview:
      "radial-gradient(120% 80% at 10% 20%, #bfdbfe 0%, transparent 55%), radial-gradient(100% 70% at 90% 80%, #c4b5fd 0%, transparent 50%), #f0f7ff",
  },
  dots: {
    id: "dots",
    label: "Points",
    preview:
      "radial-gradient(circle at 1px 1px, rgb(100 116 139 / 0.18) 1px, transparent 0) 0 0 / 14px 14px, linear-gradient(180deg, #f8fafc, #eef2ff)",
  },
};

function isWallpaperId(v: unknown): v is ChatWallpaperId {
  return typeof v === "string" && (CHAT_WALLPAPER_IDS as readonly string[]).includes(v);
}

function clampOpacity(n: number): number {
  if (!Number.isFinite(n)) return DEFAULT_PREFS.opacity;
  return Math.max(0, Math.min(100, Math.round(n)));
}

function clampBlur(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

/** 0–100 → 0–16 px de flou. */
export function wallpaperBlurPx(blur: number): number {
  return Math.round((clampBlur(blur) / 100) * 16 * 10) / 10;
}

function normalizePrefs(raw: unknown): ChatWallpaperPrefs {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_PREFS };
  const o = raw as Record<string, unknown>;
  const id = isWallpaperId(o.id) ? o.id : "none";
  const customUrl =
    typeof o.customUrl === "string" && o.customUrl.startsWith("data:image/")
      ? o.customUrl
      : null;
  // custom sans image → aucun
  if (id === "custom" && !customUrl) {
    return {
      id: "none",
      opacity: clampOpacity(typeof o.opacity === "number" ? o.opacity : DEFAULT_PREFS.opacity),
      blur: clampBlur(typeof o.blur === "number" ? o.blur : 0),
      customUrl: null,
    };
  }
  return {
    id,
    opacity: clampOpacity(typeof o.opacity === "number" ? o.opacity : DEFAULT_PREFS.opacity),
    blur: clampBlur(typeof o.blur === "number" ? o.blur : 0),
    customUrl: id === "custom" ? customUrl : null,
  };
}

function readAll(): Record<string, ChatWallpaperPrefs> {
  try {
    const raw = localStorage.getItem(CHAT_WALLPAPER_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    const out: Record<string, ChatWallpaperPrefs> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof k === "string" && k) out[k] = normalizePrefs(v);
    }
    return out;
  } catch {
    return {};
  }
}

function writeAll(map: Record<string, ChatWallpaperPrefs>): boolean {
  try {
    localStorage.setItem(CHAT_WALLPAPER_STORAGE_KEY, JSON.stringify(map));
    return true;
  } catch {
    return false;
  }
}

export function readChatWallpaper(conversationId: string | null | undefined): ChatWallpaperPrefs {
  if (!conversationId) return { ...DEFAULT_PREFS };
  const all = readAll();
  return all[conversationId] ? { ...all[conversationId] } : { ...DEFAULT_PREFS };
}

export function persistChatWallpaper(
  conversationId: string,
  prefs: Partial<ChatWallpaperPrefs>
): ChatWallpaperPrefs {
  const all = readAll();
  const next = normalizePrefs({ ...readChatWallpaper(conversationId), ...prefs });
  if (
    next.id === "none" &&
    next.opacity === DEFAULT_PREFS.opacity &&
    next.blur === DEFAULT_PREFS.blur &&
    !next.customUrl
  ) {
    delete all[conversationId];
  } else {
    all[conversationId] = next;
  }
  if (!writeAll(all) && next.id === "custom" && next.customUrl) {
    throw new Error("Impossible d’enregistrer le fond (stockage plein). Réessayez une photo plus légère.");
  }
  return next;
}

/**
 * Compresse une image pour fond (JPEG, max ~1280px) — évite de saturer localStorage.
 */
export function compressWallpaperImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const looksImage =
      file.type.startsWith("image/") ||
      (!file.type && /\.(jpe?g|png|webp|gif|heic|heif|avif)$/i.test(file.name));
    if (!looksImage) {
      reject(new Error("Choisissez une image (JPEG, PNG, WebP, HEIC…)."));
      return;
    }
    if (file.size > CHAT_WALLPAPER_MAX_SOURCE_BYTES) {
      reject(new Error("Image trop volumineuse (max 10 Mo)."));
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const maxDim = 1280;
      let { width: w, height: h } = img;
      if (w > maxDim || h > maxDim) {
        const scale = maxDim / Math.max(w, h);
        w = Math.max(1, Math.round(w * scale));
        h = Math.max(1, Math.round(h * scale));
      }
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Compression impossible."));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      try {
        const dataUrl = canvas.toDataURL("image/jpeg", 0.78);
        if (dataUrl.length > 2_800_000) {
          const lighter = canvas.toDataURL("image/jpeg", 0.55);
          resolve(lighter);
          return;
        }
        resolve(dataUrl);
      } catch {
        reject(new Error("Enregistrement de l’image impossible."));
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Image illisible."));
    };
    img.src = url;
  });
}
